import dayjs from 'dayjs';

import { AppRoutes } from 'services/routes';

export const ReferralTransitionMatchRole = {
    PRIOR: 'PRIOR',
    NEW: 'NEW',
};

export const TransitionViewContext = {
    TREATMENT: 'treatment',
    REFERRAL: 'referral',
};

const formatBrandType = (isBrand) => (isBrand ? 'Brand' : 'Generic');

const getMostRecentTransition = (transitions) => {
    if (!transitions?.length) {
        return undefined;
    }

    return transitions.reduce((mostRecent, transition) =>
        dayjs(transition.transitionStart).isAfter(dayjs(mostRecent.transitionStart))
            ? transition
            : mostRecent,
    );
};

const getTransitionProductInfo = (transition) => {
    const isPriorRole = transition.matchedReferralRole === ReferralTransitionMatchRole.PRIOR;

    if (transition.isPending || isPriorRole) {
        return {
            productServiceOid: transition.requestedProductServiceOid,
            isBrand: transition.requestedIsBrand,
        };
    }

    return {
        productServiceOid: transition.priorProductServiceOid,
        isBrand: transition.priorIsBrand,
    };
};

const getTransitionLinkTarget = (transition, viewContext, payerId) => {
    const isPriorRole = transition.matchedReferralRole === ReferralTransitionMatchRole.PRIOR;

    if (viewContext === TransitionViewContext.TREATMENT) {
        const treatmentId = isPriorRole ? transition.newTreatmentId : transition.priorTreatmentId;

        if (!treatmentId) {
            return null;
        }

        return {
            url: AppRoutes.treatmentDetails(payerId, treatmentId),
            label: 'Go to Treatment',
        };
    }

    const linkedReferralId = isPriorRole ? transition.newReferralId : transition.priorReferralId;

    if (linkedReferralId) {
        return {
            url: AppRoutes.referralDetails(payerId, linkedReferralId),
            label: 'Go to Referral',
        };
    }

    if (transition.isPending && transition.newTreatmentId) {
        return {
            url: AppRoutes.treatmentDetails(payerId, transition.newTreatmentId),
            label: 'Go to Treatment',
        };
    }

    return null;
};

export const getTransitionMessage = ({
    transitions,
    productServiceMap,
    payerId = undefined,
    viewContext = undefined,
}) => {
    const resolvedTransition = getMostRecentTransition(transitions);

    if (!resolvedTransition) {
        return null;
    }

    const { productServiceOid, isBrand } = getTransitionProductInfo(resolvedTransition);
    const productName = productServiceMap?.[productServiceOid];

    if (!productName) {
        return null;
    }

    const brandType = formatBrandType(isBrand);
    const isPriorRole =
        resolvedTransition.matchedReferralRole === ReferralTransitionMatchRole.PRIOR;
    const productLabel = `${productName} (${brandType})`;

    let message;
    if (resolvedTransition.isPending) {
        message = `Conversion to ${productLabel} pending.`;
    } else if (isPriorRole) {
        message = `Converted to ${productLabel}.`;
    } else {
        message = `Converted from ${productLabel}.`;
    }

    const linkTarget =
        payerId && viewContext
            ? getTransitionLinkTarget(resolvedTransition, viewContext, payerId)
            : null;

    return { message, linkTarget };
};

export const shouldShowTherapyTransitionMessage = (transitions, productServiceMap) =>
    !!getTransitionMessage({ transitions, productServiceMap })?.message;

export const BIOSIMILAR_CONVERSION_IN_PROGRESS_MESSAGE = 'Conversion already in progress';

export const isBiosimilarConversionInProgress = (transitions) =>
    !!transitions?.some((transition) => transition.isPending);

export const getConvertBiosimilarErrorMessage = (err) =>
    err?.status === 409
        ? BIOSIMILAR_CONVERSION_IN_PROGRESS_MESSAGE
        : 'Unable to convert biosimilar. Please try again.';
